import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Users, Briefcase, X, Send, Award, Building2, Bookmark, BookmarkCheck } from '../components/CustomIcons';
import EmptyState from '../components/EmptyState';
import EmployerOnboardingModal from '../components/EmployerOnboardingModal';
import { toast } from '../components/ui/sonner';

// ── 6 hardcoded demo profiles (frontend-only, not seeded to DB) ───────────────
const DEMO_CANDIDATES = [
  {
    candidate_id: 'DEMO-01',
    display_name: 'Marcus T.',
    credentials: [],
    completion_pct: 0,
    current_module: 'HVAC Fundamentals',
    availability: 'Available: Q1 2027',
    readiness_status: 'Just enrolled · credential in progress',
    readiness_level: 'training',
  },
  {
    candidate_id: 'DEMO-02',
    display_name: 'James W.',
    credentials: [{ title: 'EPA 608 Certification', verified: true }],
    completion_pct: 33,
    current_module: 'HVAC Fundamentals',
    availability: 'Available: Q3 2026',
    readiness_status: 'On track · 1 credential earned',
    readiness_level: 'progress',
  },
  {
    candidate_id: 'DEMO-03',
    display_name: 'Darius M.',
    credentials: [{ title: 'CompTIA AI+', verified: true }],
    completion_pct: 55,
    current_module: 'AI Foundations',
    availability: 'Available: Q2 2026',
    readiness_status: 'On track · credential in progress',
    readiness_level: 'progress',
  },
  {
    candidate_id: 'DEMO-04',
    display_name: 'Sandra R.',
    credentials: [
      { title: 'NHA CCMA', verified: true },
      { title: 'OSHA-10', verified: true },
    ],
    completion_pct: 74,
    current_module: 'Medical Assistant Essentials',
    availability: 'Available: Q1 2026',
    readiness_status: 'Reentry-ready · 2 credentials earned',
    readiness_level: 'ready',
  },
  {
    candidate_id: 'DEMO-05',
    display_name: 'Anthony B.',
    credentials: [
      { title: 'EPA 608 Certification', verified: true },
      { title: 'NCCER Core', verified: true },
    ],
    completion_pct: 90,
    current_module: 'HVAC Fundamentals',
    availability: 'Available now',
    readiness_status: 'Reentry-ready · 2 credentials earned',
    readiness_level: 'ready',
  },
  {
    candidate_id: 'DEMO-06',
    display_name: 'Kevin L.',
    credentials: [
      { title: 'CompTIA AI+', verified: true },
      { title: 'AWS Cloud Practitioner', verified: true },
      { title: 'NCCER Core', verified: true },
    ],
    completion_pct: 100,
    current_module: 'AI Foundations',
    availability: 'Available now',
    readiness_status: 'Reentry-ready · 3 credentials earned',
    readiness_level: 'ready',
  },
];

// ── Completion ring ────────────────────────────────────────────────────────────
function CompletionRing({ pct, size = 52 }) {
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const color = pct >= 75 ? '#22C55E' : pct >= 40 ? '#F97316' : '#94A3B8';
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E2E8F0" strokeWidth={5} />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke={color} strokeWidth={5}
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
      />
      <text
        x={size / 2} y={size / 2 + 1}
        textAnchor="middle" dominantBaseline="middle"
        fill={color} fontSize={10} fontWeight={700}
        fontFamily="Plus Jakarta Sans, sans-serif"
        style={{ transform: `rotate(90deg) translate(0, -${size}px)`, transformOrigin: `${size / 2}px ${size / 2}px` }}
      >
        {pct}%
      </text>
    </svg>
  );
}

// ── Single candidate card ──────────────────────────────────────────────────────
function CandidateCard({ candidate, onRequestIntro, isDemoMode, isSaved, onToggleSave }) {
  const { candidate_id, display_name, credentials, completion_pct, current_module, availability, readiness_status, readiness_level } = candidate;
  const readinessColor = readiness_level === 'ready' ? '#F97316' : readiness_level === 'progress' ? '#F97316' : '#94A3B8';
  const [savePending, setSavePending] = useState(false);

  const handleSave = async (e) => {
    e.stopPropagation();
    if (savePending) return;
    setSavePending(true);
    await onToggleSave(candidate);
    setSavePending(false);
  };

  return (
    <div
      data-testid={`marketplace-card-${candidate_id}`}
      style={{
        background: '#FFFFFF',
        border: `1px solid ${isSaved ? 'rgba(249,115,22,0.3)' : '#E2E8F0'}`,
        borderRadius: '16px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        boxShadow: isSaved ? '0 2px 8px rgba(249,115,22,0.08)' : '0 1px 4px rgba(15,39,68,0.06)',
        transition: 'box-shadow 0.2s, transform 0.2s, border-color 0.2s',
        position: 'relative',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(15,39,68,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = isSaved ? '0 2px 8px rgba(249,115,22,0.08)' : '0 1px 4px rgba(15,39,68,0.06)'; e.currentTarget.style.transform = 'none'; }}
    >
      {/* Bookmark button */}
      <button
        onClick={handleSave}
        disabled={savePending}
        data-testid={`bookmark-btn-${candidate_id}`}
        aria-label={isSaved ? `Unsave ${display_name}` : `Save ${display_name}`}
        style={{
          position: 'absolute', top: '16px', right: '16px',
          background: isSaved ? 'rgba(249,115,22,0.08)' : 'transparent',
          border: 'none', cursor: 'pointer',
          color: isSaved ? '#F97316' : '#CBD5E1',
          borderRadius: '8px', padding: '5px',
          transition: 'all 0.15s', lineHeight: 0,
        }}
        onMouseEnter={e => { e.currentTarget.style.color = '#F97316'; e.currentTarget.style.background = 'rgba(249,115,22,0.08)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = isSaved ? '#F97316' : '#CBD5E1'; e.currentTarget.style.background = isSaved ? 'rgba(249,115,22,0.08)' : 'transparent'; }}
      >
        {isSaved ? <BookmarkCheck size={17} /> : <Bookmark size={17} />}
      </button>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', paddingRight: '28px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '18px', color: '#0F2744', marginBottom: '4px' }}>
            {display_name}
          </div>
          <div style={{ fontSize: '13px', color: '#64748B' }}>{current_module}</div>
        </div>
        <div style={{ textAlign: 'center', flexShrink: 0 }}>
          <CompletionRing pct={completion_pct} />
          <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '2px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>complete</div>
        </div>
      </div>

      {/* Verified credentials */}
      {credentials.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {credentials.map(cred => (
            <span
              key={cred.title}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)',
                color: '#15803D', fontSize: '11px', fontWeight: 700,
                padding: '3px 8px', borderRadius: '6px',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
              }}
            >
              <CheckCircle size={10} />
              {cred.title}
              <span style={{ background: 'rgba(34,197,94,0.15)', color: '#15803D', borderRadius: '4px', padding: '1px 5px', fontSize: '9px', fontWeight: 800, letterSpacing: '0.04em' }}>VERIFIED</span>
            </span>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', color: '#94A3B8', fontSize: '11px', padding: '3px 8px', borderRadius: '6px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Credential in progress
          </span>
        </div>
      )}

      {/* Availability + Readiness */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px solid #F1F5F9' }}>
        <span style={{ fontSize: '12px', color: '#64748B', fontFamily: 'Inter, sans-serif' }}>{availability}</span>
        <span style={{ fontSize: '12px', fontWeight: 700, color: readinessColor, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {readiness_status}
        </span>
      </div>

      {/* CTA */}
      <button
        data-testid={`request-intro-btn-${candidate_id}`}
        onClick={() => !isDemoMode && onRequestIntro(candidate)}
        style={{
          background: '#0F2744',
          color: '#FFFFFF',
          border: 'none',
          borderRadius: '10px',
          padding: '12px 20px',
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          fontWeight: 700,
          fontSize: '14px',
          cursor: isDemoMode ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          opacity: isDemoMode ? 0.6 : 1,
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { if (!isDemoMode) e.currentTarget.style.background = '#1E3A5F'; }}
        onMouseLeave={e => { if (!isDemoMode) e.currentTarget.style.background = '#0F2744'; }}
      >
        <Send size={14} /> Request Introduction
      </button>
    </div>
  );
}

// ── Request Introduction Modal ─────────────────────────────────────────────────
function RequestIntroModal({ candidate, onClose, onSubmit }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async () => {
    setSending(true);
    await onSubmit(candidate.candidate_id, message);
    setSending(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,39,68,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ background: '#FFFFFF', borderRadius: '20px', padding: '32px', width: '100%', maxWidth: '480px', boxShadow: '0 24px 64px rgba(15,39,68,0.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '18px', color: '#0F2744', margin: 0 }}>
            Request Introduction
          </h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: '4px' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ background: '#F8FAFC', borderRadius: '10px', padding: '14px', marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(15,39,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: '#0F2744', flexShrink: 0 }}>
            {candidate.display_name[0]}
          </div>
          <div>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: '#0F2744' }}>{candidate.display_name}</div>
            <div style={{ fontSize: '12px', color: '#64748B' }}>{candidate.current_module} · {candidate.availability}</div>
          </div>
        </div>

        <p style={{ fontSize: '13px', color: '#64748B', marginBottom: '16px', lineHeight: 1.6 }}>
          Your request will be sent to the participant's case manager, who will coordinate the next steps. You will not contact the participant directly at this stage.
        </p>

        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', letterSpacing: '0.06em' }}>
          MESSAGE TO CASE MANAGER <span style={{ color: '#94A3B8', fontWeight: 400 }}>(optional)</span>
        </label>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Describe the role, your timeline, or any questions for the case manager..."
          rows={3}
          data-testid="intro-message-input"
          style={{ width: '100%', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '10px 14px', fontSize: '14px', color: '#0F2744', outline: 'none', resize: 'vertical', fontFamily: 'Inter, sans-serif', boxSizing: 'border-box', marginBottom: '20px' }}
        />

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onClose} style={{ flex: 1, background: 'transparent', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '12px', cursor: 'pointer', color: '#475569', fontSize: '14px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={sending}
            data-testid="intro-submit-btn"
            style={{ flex: 2, background: '#F97316', border: 'none', borderRadius: '10px', padding: '12px', cursor: 'pointer', color: '#0F2744', fontWeight: 700, fontSize: '14px', fontFamily: 'Plus Jakarta Sans, sans-serif', opacity: sending ? 0.7 : 1 }}>
            <Send size={14} style={{ display: 'inline', marginRight: '6px' }} />
            {sending ? 'Sending…' : 'Send Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Marketplace page ──────────────────────────────────────────────────────
export default function Marketplace() {
  const { authFetch, user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [introTarget, setIntroTarget] = useState(null);
  const [requestedIds, setRequestedIds] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('aos_intro_requested') || '[]'); } catch { return []; }
  });

  // Saved candidates: set of candidate_ids
  const [savedIds, setSavedIds] = useState(new Set());
  const [savedCandidates, setSavedCandidates] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'saved'

  // Demo Mode: lives in sessionStorage
  const [isDemoMode, setIsDemoMode] = useState(() => sessionStorage.getItem('aos_employer_demo_mode') === '1');

  // Onboarding modal
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  const allCandidates = isDemoMode ? DEMO_CANDIDATES : candidates;
  const displayCandidates = activeTab === 'saved'
    ? (isDemoMode ? DEMO_CANDIDATES.filter(c => savedIds.has(c.candidate_id)) : savedCandidates)
    : allCandidates;

  const toggleDemo = () => {
    const next = !isDemoMode;
    sessionStorage.setItem('aos_employer_demo_mode', next ? '1' : '0');
    setIsDemoMode(next);
  };

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch('/marketplace/candidates');
      setCandidates(r.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [authFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSaved = useCallback(async () => {
    try {
      const r = await authFetch('/marketplace/saved');
      const data = r.data || [];
      setSavedCandidates(data);
      setSavedIds(new Set(data.map(c => c.candidate_id)));
    } catch { /* ignore */ }
  }, [authFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadCandidates();
    loadSaved();
    if (user?.role === 'employer_partner') {
      authFetch('/marketplace/onboard/status').then(r => {
        if (!r.data?.onboarding_complete) setShowOnboarding(true);
      }).catch(() => {}).finally(() => setOnboardingChecked(true));
    } else {
      setOnboardingChecked(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleSave = async (candidate) => {
    const cid = candidate.candidate_id;
    // Optimistic update
    const wasaved = savedIds.has(cid);
    setSavedIds(prev => {
      const next = new Set(prev);
      wasaved ? next.delete(cid) : next.add(cid);
      return next;
    });
    if (!wasaved) {
      setSavedCandidates(prev => [{ ...candidate }, ...prev]);
    } else {
      setSavedCandidates(prev => prev.filter(c => c.candidate_id !== cid));
    }
    try {
      await authFetch('/marketplace/saved', {
        method: 'POST',
        data: { candidate_id: cid, candidate_data: candidate },
      });
      toast.success(wasaved ? `${candidate.display_name} removed from saved` : `${candidate.display_name} saved to shortlist`);
    } catch {
      // Revert on failure
      setSavedIds(prev => {
        const next = new Set(prev);
        wasaved ? next.add(cid) : next.delete(cid);
        return next;
      });
      toast.error('Could not save. Please try again.');
    }
  };

  const handleRequestIntro = async (candidateId, message) => {
    try {
      await authFetch('/marketplace/introductions', {
        method: 'POST',
        data: { candidate_id: candidateId, message },
      });
      const updated = [...requestedIds, candidateId];
      setRequestedIds(updated);
      sessionStorage.setItem('aos_intro_requested', JSON.stringify(updated));
      setIntroTarget(null);
      toast.success('Introduction requested! Your case manager will be in touch.');
    } catch {
      toast.error('Failed to send request. Please try again.');
    }
  };

  const handleOnboardComplete = async (data) => {
    try {
      await authFetch('/marketplace/onboard', { method: 'POST', data });
      setShowOnboarding(false);
      toast.success('Welcome to the talent marketplace!');
    } catch {
      toast.error('Could not save profile. Please try again.');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }} data-testid="employer-marketplace">
      {/* Demo Mode Banner */}
      {isDemoMode && (
        <div data-testid="demo-mode-banner" style={{ background: '#F97316', color: '#0F2744', padding: '8px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px' }}>
          <span>Live Demo · AssistivOS — Sample profiles shown. No real data.</span>
          <button onClick={toggleDemo} data-testid="exit-demo-btn" style={{ background: 'rgba(15,39,68,0.15)', border: 'none', borderRadius: '6px', padding: '4px 12px', cursor: 'pointer', color: '#0F2744', fontWeight: 700, fontSize: '12px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Exit Demo
          </button>
        </div>
      )}

      <div style={{ maxWidth: '1140px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Page header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '28px', color: 'var(--text-primary)', margin: '0 0 6px 0' }}>
              Talent Marketplace
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>
              Credential-verified, workforce-ready participants. Introductions brokered by case managers.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {!isDemoMode && (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', fontSize: '13px', color: 'var(--text-muted)' }}>
                <Users size={14} color="#F97316" />
                <span>{candidates.length} participant{candidates.length !== 1 ? 's' : ''} available</span>
              </div>
            )}
            <button
              onClick={toggleDemo}
              data-testid="demo-mode-toggle-btn"
              style={{
                background: isDemoMode ? '#F97316' : 'var(--bg-surface)',
                border: isDemoMode ? 'none' : '1px solid var(--border-subtle)',
                borderRadius: '10px', padding: '9px 16px', cursor: 'pointer',
                color: isDemoMode ? '#0F2744' : 'var(--text-secondary)',
                fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              <Briefcase size={13} />
              {isDemoMode ? 'Exit Demo' : 'Demo Mode'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', background: 'var(--bg-surface)', padding: '4px', borderRadius: '12px', width: 'fit-content', border: '1px solid var(--border-subtle)' }}>
          {[
            { key: 'all', label: `All Candidates${!isDemoMode && candidates.length ? ` (${candidates.length})` : isDemoMode ? ' (6)' : ''}` },
            { key: 'saved', label: `Saved${savedIds.size > 0 ? ` (${savedIds.size})` : ''}` },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
              style={{
                background: activeTab === tab.key ? '#FFFFFF' : 'transparent',
                border: 'none', borderRadius: '9px',
                padding: '8px 18px', cursor: 'pointer',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontWeight: activeTab === tab.key ? 700 : 500,
                fontSize: '13px',
                color: activeTab === tab.key ? '#0F2744' : 'var(--text-secondary)',
                boxShadow: activeTab === tab.key ? '0 1px 4px rgba(15,39,68,0.1)' : 'none',
                transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              {tab.key === 'saved' && <Bookmark size={12} />}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading && !isDemoMode ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '24px', height: '240px', animation: 'pulse 1.5s ease-in-out infinite' }} />
            ))}
          </div>
        ) : activeTab === 'saved' && savedIds.size === 0 ? (
          <EmptyState
            icon={Bookmark}
            title="No saved candidates yet"
            subtitle="Tap the bookmark icon on any candidate card to add them to your shortlist."
            testId="saved-empty"
          />
        ) : displayCandidates.length === 0 ? (
          <EmptyState
            icon={Award}
            title="No participants in the marketplace yet"
            subtitle="Participants appear here when they earn a verified credential or complete 50%+ of their pathway."
            testId="marketplace-empty"
          />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
            {displayCandidates.map(c => (
              <CandidateCard
                key={c.candidate_id}
                candidate={c}
                onRequestIntro={setIntroTarget}
                isDemoMode={isDemoMode || requestedIds.includes(c.candidate_id)}
                isSaved={savedIds.has(c.candidate_id)}
                onToggleSave={handleToggleSave}
              />
            ))}
          </div>
        )}

        {/* Trust note */}
        <div style={{ marginTop: '40px', padding: '16px 20px', background: 'rgba(15,39,68,0.04)', border: '1px solid rgba(15,39,68,0.08)', borderRadius: '12px', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Building2 size={16} color="#0F2744" style={{ flexShrink: 0 }} />
          <p style={{ fontSize: '13px', color: '#475569', margin: 0, lineHeight: 1.5 }}>
            Participant names are shown as first name and last initial only. Case managers broker all introductions — you will not contact participants directly at this stage.
          </p>
        </div>
      </div>

      {/* Request Introduction Modal */}
      {introTarget && (
        <RequestIntroModal
          candidate={introTarget}
          onClose={() => setIntroTarget(null)}
          onSubmit={handleRequestIntro}
        />
      )}

      {/* Employer Onboarding Modal (first visit) */}
      {showOnboarding && onboardingChecked && (
        <EmployerOnboardingModal
          onComplete={handleOnboardComplete}
          onDismiss={() => setShowOnboarding(false)}
        />
      )}
    </div>
  );
}
